import * as Turbo from '@hotwired/turbo'
import { Controller } from 'stimulus'

import { useWebSocket, WebSocketState } from '@blueberry/framework/web_sockets'

// How long to wait for the calendar invite before giving up on the booking.
const ROLLBACK_TIMEOUT_MS = 15_000

// Keep the button's loading state up at least this long so a fast invite
// doesn't flash it.
const MIN_DISPLAY_MS = 2_000

type BookingWsPayload = {
  action: string
  appointment_uuid?: string
}

// Books the chosen slot over fetch while the submit button sits in a disabled
// "Booking visit…" state and the rest of the screen is inert (pointer events
// off, Turbo navigation blocked), then waits for the Cloud Task to finish
// creating the calendar invite (broadcast over WebSocket) before moving to
// the confirmation page. If the invite doesn't arrive in time, rolls the
// booking back and follows the redirect to the error message.
export default class extends Controller<HTMLElement> {
  static targets = ['form']
  static values = { hashedParentId: String }

  declare readonly formTarget: HTMLFormElement
  declare readonly hashedParentIdValue: string

  private ws: WebSocketState<unknown> | null = null
  private timeoutId: number | null = null
  private navigateTimerId: number | null = null
  private appointmentUuid: string | null = null
  private bookedUrl: string | null = null
  private submittedAt = 0
  private pending = false
  private allowNavigation = false
  // The broadcast can beat the fetch response (which follows the redirect and
  // renders the booked page), so buffer uuids until we know our appointment.
  private broadcastUuids = new Set<string>()
  // While the booking is in flight the screen has no modal guarding it, so
  // block any Turbo navigation we didn't start ourselves (back arrow, dock).
  private blockNavigationWhilePending = (event: Event) => {
    if (this.pending && !this.allowNavigation) {
      event.preventDefault()
    }
  }

  // Undo the pending state before Turbo snapshots the page, so a back-button
  // restore doesn't resurrect a disabled button on an inert screen.
  private endPendingStateBeforeCache = () => {
    this.pending = false
    this.element.classList.remove('pointer-events-none')
    const submitButton = this.submitButton
    if (submitButton) {
      submitButton.disabled = false
    }
  }

  connect() {
    document.addEventListener(
      'turbo:before-visit',
      this.blockNavigationWhilePending,
    )
    document.addEventListener(
      'turbo:before-cache',
      this.endPendingStateBeforeCache,
    )
    this.ws = useWebSocket<BookingWsPayload, unknown>({
      url: `/ws/chat/${this.hashedParentIdValue}/`,
      onMessage: (payload) => {
        if (
          payload.action !== 'SEND_BALANCE_BOOKING_RESULT' ||
          payload.appointment_uuid === undefined
        ) {
          return
        }
        this.broadcastUuids.add(payload.appointment_uuid)
        this.goToConfirmationIfReady()
      },
    })
  }

  disconnect() {
    document.removeEventListener(
      'turbo:before-visit',
      this.blockNavigationWhilePending,
    )
    document.removeEventListener(
      'turbo:before-cache',
      this.endPendingStateBeforeCache,
    )
    if (this.timeoutId !== null) {
      window.clearTimeout(this.timeoutId)
    }
    if (this.navigateTimerId !== null) {
      window.clearTimeout(this.navigateTimerId)
    }
    this.ws?.teardown()
  }

  async submit(event: SubmitEvent) {
    event.preventDefault()
    this.submittedAt = performance.now()
    this.startPendingState()

    let response: Response
    try {
      response = await fetch(this.formTarget.action, {
        method: 'POST',
        body: new FormData(this.formTarget),
      })
    } catch {
      window.location.reload()
      return
    }

    // Success redirects to the booked page; anything else (slot taken,
    // timezone missing) redirects back with a flash message. The fetch already
    // consumed that render, so show the HTML we have instead of refetching.
    const bookedMatch = response.url.match(
      /\/visits\/booked\/([0-9a-fA-F-]{36})/,
    )
    if (!bookedMatch) {
      await this.renderFetchedPage(response)
      return
    }

    this.appointmentUuid = bookedMatch[1]
    this.bookedUrl = response.url
    this.timeoutId = window.setTimeout(
      () => void this.rollback(),
      ROLLBACK_TIMEOUT_MS,
    )
    this.goToConfirmationIfReady()
  }

  // Disable the submit button (its disabled styles render the "Booking
  // visit…" state, all markup server-side) and turn pointer events off on the
  // form area so the snapshotted form data can't change mid-flight. The
  // header/footer sit outside this element — the turbo:before-visit blocker
  // covers navigation from there.
  private startPendingState() {
    this.pending = true
    this.element.classList.add('pointer-events-none')
    const submitButton = this.submitButton
    if (submitButton) {
      submitButton.disabled = true
    }
  }

  // The footer button lives outside this controller's element and associates
  // via the form attribute, so reach it through the form's elements.
  private get submitButton(): HTMLButtonElement | null {
    for (const element of Array.from(this.formTarget.elements)) {
      if (element instanceof HTMLButtonElement && element.type === 'submit') {
        return element
      }
    }
    return null
  }

  private goToConfirmationIfReady() {
    if (this.appointmentUuid === null || this.bookedUrl === null) return
    if (!this.broadcastUuids.has(this.appointmentUuid)) return
    if (this.navigateTimerId !== null) return

    // The invite is ready — the rollback can't fire anymore.
    if (this.timeoutId !== null) {
      window.clearTimeout(this.timeoutId)
      this.timeoutId = null
    }

    const bookedUrl = this.bookedUrl
    const remaining = MIN_DISPLAY_MS - (performance.now() - this.submittedAt)
    this.navigateTimerId = window.setTimeout(
      () => {
        // Refetch via Turbo (no full reload): the HTML from the booking fetch
        // predates the Meet link, so it can't be reused here.
        this.visit(bookedUrl)
      },
      Math.max(0, remaining),
    )
  }

  private async rollback() {
    if (this.bookedUrl === null) return
    // The rollback route is the booked URL + "/rollback"; derive it from the
    // booked URL we already have rather than carrying a server-rendered URL
    // template with a placeholder id through the page.
    const url = new URL(this.bookedUrl)
    url.pathname += '/rollback'
    const body = new FormData()
    const csrf = this.formTarget.querySelector<HTMLInputElement>(
      'input[name=csrfmiddlewaretoken]',
    )
    if (csrf) {
      body.append('csrfmiddlewaretoken', csrf.value)
    }
    try {
      const response = await fetch(url, { method: 'POST', body })
      // Rolled back: the slot screen shows the error. If the invite landed in
      // the meantime, rollback no-ops and redirects to the confirmation instead.
      await this.renderFetchedPage(response)
    } catch {
      window.location.reload()
    }
  }

  // Render an already-fetched page through Turbo — keeps the app shell mounted
  // (no full-document reload) and preserves one-shot flash messages that the
  // fetch's redirect-follow already consumed.
  private async renderFetchedPage(response: Response) {
    this.allowNavigation = true
    Turbo.visit(response.url, {
      action: 'replace',
      response: {
        statusCode: response.status,
        redirected: response.redirected,
        responseHTML: await response.text(),
      },
    })
  }

  // Our own navigations must slip past the pending-state blocker.
  private visit(url: string) {
    this.allowNavigation = true
    Turbo.visit(url)
  }
}
